import { useQuery } from '@tanstack/react-query';
import { onrampManifest } from '../generated/manifest.js';
import { directPurchaseCredentials } from './credentials.js';
import { discoverDirectPurchaseMethods } from './discovery.js';
import { directPurchaseMethods } from './eligibility.js';
/** Discovery never creates an operation or opens a provider authentication frame. */
export function useDirectPurchaseContext(options) {
  const {
    client,
    purchase,
    wallet,
    cashD6
  } = options;
  const eligibleBalance = cashD6 !== null && /^\d{1,20}$/.test(cashD6) && BigInt(cashD6) < BigInt(onrampManifest.direct_token_purchase.settings.buy_action_threshold_usdc_d6);
  const enabled = Boolean(client && options.provider && purchase && wallet && eligibleBalance && options.onrampEnabled === true && options.directPurchaseEnabled === true);
  const query = useQuery({
    queryKey: ['onramp', client?.cacheKey, 'direct-purchase', options.sessionProofId, options.network, options.surface, options.deviceMethods, wallet, purchase],
    enabled,
    staleTime: 30000,
    gcTime: 60000,
    retry: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    meta: {
      persist: false
    },
    queryFn: async ({
      signal
    }) => {
      if (!client || !purchase || !options.provider) throw new Error('onramp_purchase_context_missing');
      const controller = new AbortController();
      const cancel = () => controller.abort();
      signal.addEventListener('abort', cancel, {
        once: true
      });
      const timeout = new Promise((_, reject) => {
        controller.signal.addEventListener('abort', () => reject(new Error('onramp_purchase_discovery_aborted')), {
          once: true
        });
      });
      const timer = setTimeout(cancel, 10000);
      try {
        if (signal.aborted) controller.abort();
        const credentials = await Promise.race([directPurchaseCredentials(options.provider, controller.signal), timeout]);
        const context = await Promise.race([client.getDirectPurchaseContext(purchase, options.surface, controller.signal, credentials), timeout]);
        if (!context.purchase || context.reason) return context;
        const available = await Promise.race([discoverDirectPurchaseMethods(options.provider, controller.signal), timeout]);
        return {
          ...context,
          methods: context.methods.filter(method => available.includes(method))
        };
      } finally {
        clearTimeout(timer);
        signal.removeEventListener('abort', cancel);
      }
    }
  });
  const context = enabled && !query.isError ? query.data : undefined;
  const methods = directPurchaseMethods(onrampManifest.direct_token_purchase, {
    onrampEnabled: options.onrampEnabled === true,
    directPurchaseEnabled: options.directPurchaseEnabled === true,
    cashD6,
    ownedWallet: Boolean(wallet && context?.destination === wallet),
    tokenSupported: Boolean(context?.purchase && context.purchase.network === options.network && !context.reason),
    surface: options.surface,
    country: context?.country ?? null,
    subdivision: context?.subdivision ?? null,
    deviceMethods: options.deviceMethods,
    providerMethods: context?.methods ?? []
  });
  return {
    ...query,
    context,
    methods
  };
}